import { apiService } from "@base/configs";
import httpService from "@base/services/httpService";
import { IFilterMySpecialPackage } from "./filterMySpecialPackage.interface";
import { ISpecialPackage, ISpecialPackageDetail, ISpecialPackageDetailImplement, ISpecialPackagePage } from "./patientSpecialPackage.interface";

/** The two write endpoints of the package flow want this vendor type, as the apps send it. */
const MARKET_SCAN_HEADERS = { "Content-Type": "application/vnd.market-scan.v1+json", Accept: "application/json" };

class PatientSpecialPackageService {
  calculateFee(body: any) {
    return httpService.post(apiService.patient.patientSpecialPackage.calculateFee, body);
  }

  buy(body: any) {
    return httpService.post(apiService.patient.patientSpecialPackage.buy, body);
  }

  /**
   * The per-record endpoint only answers for one patient; `my-packages` covers the whole account.
   * Which one applies is decided by whether a record was picked, so callers use `findAll` either way.
   */
  findAll(params?: IFilterMySpecialPackage) {
    const { patient_record_code, ...rest } = params ?? {};
    const url = patient_record_code ? apiService.patient.patientSpecialPackage.findAll : apiService.patient.patientSpecialPackage.myPackages;

    return httpService.get<ISpecialPackagePage>(url, {
      params: patient_record_code ? params : rest,
      headers: {
        platform: "web",
      },
    });
  }

  /**
   * Confirms which items of a group checkup the patient takes - mandatory ones included. The body is
   * a bare JSON array of item ids, and the server answers with the updated package. It only accepts
   * this once; a second call is refused with "Service items already confirmed !".
   */
  selectServiceItems(key: string, itemIds: number[]) {
    return httpService.post<ISpecialPackage>(apiService.patient.patientSpecialPackage.selectServiceItems, itemIds, {
      params: { key },
      headers: MARKET_SCAN_HEADERS,
    });
  }

  /** Opens a new use of one service; its `id` is what the booking is made against. */
  createDetailImplement(serviceId: string | number, sequence: number, expectedDate: string, lang: string) {
    return httpService.post<ISpecialPackageDetailImplement>(
      apiService.patient.patientSpecialPackage.createDetailImplement,
      {
        appt_key: "",
        expected_date: expectedDate,
        id: 0,
        implemented_by: "",
        implemented_by_name: "",
        implemented_date: "",
        lang,
        patient_special_package_detail_id: Number(serviceId),
        sequence,
        status: "PENDING",
      },
      { headers: MARKET_SCAN_HEADERS },
    );
  }

  /** Claims the e-voucher of one service; answers with the service, its use now carrying `voucher`. */
  claimEVoucher(serviceId: string | number) {
    return httpService.post<ISpecialPackageDetail>(apiService.patient.patientSpecialPackage.claimEVoucher, undefined, {
      params: { patient_special_package_detail_id: serviceId },
    });
  }

  // The package detail resolves a lot of linked data server-side and regularly runs past the
  // default 12s, so it gets its own budget.
  getDetail(key: string) {
    return httpService.get<ISpecialPackage>(apiService.patient.patientSpecialPackage.getDetails, { params: { key }, timeout: 60000 });
  }
}

export default new PatientSpecialPackageService();
